import React, { useEffect, useState, memo } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { Tooltip } from '../components/ui/Tooltip';
import { AttachmentsField, AttachmentsList } from '../components/ui/AttachmentsField';
import { Plus, Filter, Download, Eye, Edit, Search, Wrench, CheckCircle2, X, Save, Mail, Calendar, Trash2, AlertCircle, FileText } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
const initialEquipmentData = Array.from({
  length: 50
}, (_, i) => ({
  id: String(i + 1),
  code: `EQP-${String(i + 1).padStart(3, '0')}`,
  name: i % 2 === 0 ? `Fonometro ${i}` : `Stampante ${i}`,
  type: i % 4 === 0 ? 'Strumento Misura' : i % 4 === 1 ? 'Hardware' : i % 4 === 2 ? 'Veicolo' : 'Attrezzatura',
  lastMaintenance: '2024-01-01',
  nextMaintenance: '2025-01-01',
  maintenanceType: i % 2 === 0 ? 'Ordinaria' : 'Straordinaria',
  responsible: i % 3 === 0 ? 'Alessio' : i % 3 === 1 ? 'Lorenzo' : 'Gian Franco',
  repeatability: i % 4 === 0 ? '3 mesi' : i % 4 === 1 ? '6 mesi' : i % 4 === 2 ? '1 anno' : '2 anni'
}));
const columnsConfig = [{
  key: 'code',
  label: 'Codice'
}, {
  key: 'name',
  label: 'Asset'
}, {
  key: 'type',
  label: 'Tipologia'
}, {
  key: 'lastMaintenance',
  label: 'Ultima manut.'
}, {
  key: 'nextMaintenance',
  label: 'Prox. manut.'
}, {
  key: 'responsible',
  label: 'Responsabile'
}, {
  key: 'maintenanceType',
  label: 'Tipologia manutenzione'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function EquipmentPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [maintenanceData, setMaintenanceData] = useState<any>({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  // Custom state for "Creatable Select"
  const [equipmentTypes, setEquipmentTypes] = useState(['Strumento Misura', 'Hardware', 'Veicolo', 'Attrezzatura']);
  // View modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewItem, setViewItem] = useState<any>(null);
  const {
    addNotification
  } = useNotifications();
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialEquipmentData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['name', 'type', 'code']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleView = (item: any) => {
    setViewItem(item);
    setIsViewModalOpen(true);
  };
  const handleViewToEdit = () => {
    setIsViewModalOpen(false);
    handleEdit(viewItem);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    const nextCode = `EQP-${String(initialEquipmentData.length + 1).padStart(3, '0')}`;
    setFormData({
      code: nextCode,
      sendEmailReminder: true
    });
    setIsModalOpen(true);
  };
  const handleMaintenance = (item: any) => {
    setSelectedItem(item);
    setMaintenanceData({
      date: new Date().toISOString().split('T')[0],
      type: 'Ordinaria',
      sendEmailReminder: true
    });
    setIsMaintenanceModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setShowDeleteDialog(false);
      setItemToDelete(null);
      addNotification('success', 'Eliminato', 'Asset eliminato con successo');
    }, 1500);
  };
  const handleSave = () => {
    // If a new type was entered, add it to the list
    if (formData.type && !equipmentTypes.includes(formData.type)) {
      setEquipmentTypes(prev => [...prev, formData.type]);
    }
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
      addNotification('success', 'Asset salvato', "L'asset è stato salvato con successo.");
    }, 1500);
  };
  const saveMaintenance = () => {
    addNotification('success', 'Manutenzione registrata', 'La manutenzione è stata salvata con successo.');
    setIsMaintenanceModalOpen(false);
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  return <Layout data-id="element-4685" data-mp="zazyoz6esbm5">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-4686" data-mp="za6pzsnw3c1d">
        <div data-id="element-4687" data-mp="144ug1vyjkei2">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-4688" data-mp="qbuylof78a7v">
            Asset e manutenzioni
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-4689" data-mp="va6as5syuwt9">
            Gestione asset aziendali e manutenzioni programmate.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-4690" data-mp="1j0s6cxlsaomd">
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-4692" data-mp="1mzj6ldljqmdp" />} data-id="element-4691" data-mp="1n1vjvdgd08kf">
            Esporta
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-4694" data-mp="1dzw5kswqbil6" />} data-id="element-4693" data-mp="19472wgpexlkn">
            Nuovo asset
          </Button>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-4695" data-mp="3tr0hgyqsq6lq">
        <div className="p-4 flex flex-col gap-4" data-id="element-4696" data-mp="2mjtxvuq9vog">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-4697" data-mp="2nbqpa029zgb">
            <div className="flex-1" data-id="element-4698" data-mp="3r741ya7kwf8r">
              <Input placeholder="Cerca asset..." startContent={<Search className="h-4 w-4" data-id="element-4700" data-mp="3m80xaqvigb1b" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-4699" data-mp="3r62gdbsxsacs" />
            </div>
            <div className="flex gap-2" data-id="element-4701" data-mp="3va0f3lecnj5b">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-4703" data-mp="33ghw8pl1r71x" />} data-id="element-4702" data-mp="37jwd1igt7rgf">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-4704" data-mp="3cihchfo8m73t">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="equipment" lockedColumns={['name']} data-id="element-4705" data-mp="2jtafil4flpko" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-4706" data-mp="287lr4bg66tw8">
              <Select label="Tipologia manutenzione" value={filters.maintenanceType || ''} onChange={e => setFilter('maintenanceType', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, {
            value: 'Ordinaria',
            label: 'Ordinaria'
          }, {
            value: 'Straordinaria',
            label: 'Straordinaria'
          }]} data-id="element-4707" data-mp="2i7w2dqin9r8r" />
              <Select label="Tipologia asset" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, ...equipmentTypes.map(t => ({
            value: t,
            label: t
          }))]} data-id="element-4708" data-mp="uamtgnf7g4wz" />
              <div className="col-span-full flex justify-end" data-id="element-4709" data-mp="180glfem0b5ax">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-4711" data-mp="yanl59rbeclz" />} data-id="element-4710" data-mp="18330dzb88n36">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-4712" data-mp="1qz2ogz1336hh">
        <div className="overflow-x-auto" data-id="element-4713" data-mp="1wr8n26apf9gg">
          <table className="w-full text-left border-collapse table-auto" data-id="element-4714" data-mp="1ws0agtu5e0vn">
            <thead data-id="element-4715" data-mp="1sq5em4uzkfp9">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-4716" data-mp="1sqyld5ty357e">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''}`} data-id="element-4717" data-mp="2fpl0i48nf5hu">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-4718" data-mp="2kh32mvmerc4e">
              {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-4719" data-mp="2zdkctjiv2kpd">
                  {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-4720" data-mp="3e09cbld44xvr">
                      {item.code}
                    </td>}
                  {visibleColumns.includes('name') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-4721" data-mp="341jrbjpx6xba">
                      {item.name}
                    </td>}
                  {visibleColumns.includes('type') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-4722" data-mp="2ub8mzw2bqm3v">
                      {item.type}
                    </td>}
                  {visibleColumns.includes('lastMaintenance') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-4723" data-mp="2ldy7tzmdycto">
                      {item.lastMaintenance}
                    </td>}
                  {visibleColumns.includes('nextMaintenance') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-4724" data-mp="2gd0rlskxrsut">
                      {item.nextMaintenance}
                    </td>}
                  {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-4725" data-mp="1shfajsofxbek">
                      {item.responsible}
                    </td>}
                  {visibleColumns.includes('maintenanceType') && <td className="px-4 py-4" data-id="element-4726" data-mp="1iku91hbmqifh">
                      <Badge variant={item.maintenanceType === 'Ordinaria' ? 'info' : 'warning'} data-id="element-4727" data-mp="2g79ko2hdklv4">
                        {item.maintenanceType}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-4728" data-mp="uuztgrzxefrj">
                      <div className="flex items-center gap-1" data-id="element-4729" data-mp="urkf5re2tar6">
                        <Tooltip content="Visualizza asset" data-id="element-4730" data-mp="ptahna4vpy2e">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-4731" data-mp="31ipc5nclh1m9">
                            <Eye className="h-4 w-4 text-slate-500" data-id="element-4732" data-mp="3bf22cb8xkxc9" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Registra manutenzione" data-id="element-4733" data-mp="3l6g99437ozff">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleMaintenance(item)} data-id="element-4734" data-mp="3l8965ro0w5a8">
                            <Wrench className="h-4 w-4 text-blue-500" data-id="element-4735" data-mp="2ziho0mbsn8t" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Modifica asset" data-id="element-4736" data-mp="1zuy4adtgzepg">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-4737" data-mp="24szj2mjv4rv4">
                            <Edit className="h-4 w-4 text-seform-orange" data-id="element-4738" data-mp="3qcbic7a7zifh" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Elimina asset" data-id="element-4739" data-mp="43sbxcx8ohw1">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-4740" data-mp="3cik3qkhmt8cw">
                            <Trash2 className="h-4 w-4 text-red-500" data-id="element-4741" data-mp="3lbnpdg2fkrim" />
                          </Button>
                        </Tooltip>
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-4742" data-mp="2os6ltmsrgpw1">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-4743" data-mp="2ep1c2zw78smi" />
        </div>
      </Card>

      {/* View/Consultation Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title={viewItem ? `Dettaglio asset — ${viewItem.code}` : ''} size="2xl" data-id="element-4744" data-mp="29peyuqgdfcdp">
        {viewItem && <div className="space-y-8" data-id="element-4745" data-mp="32dkhn7nv6raw">
            <section data-id="element-4746" data-mp="32cj5t0z0y0s4">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4747" data-mp="2nhczecw0nzy9">
                <Wrench className="h-4 w-4" data-id="element-4748" data-mp="2sfwjs8uyp3oi" /> Dettagli asset
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4749" data-mp="1c268oi5fdf5d">
                <div data-id="element-4750" data-mp="1c4ajlk1qref5">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-4751" data-mp="1h19cy0dkzhca">
                    Codice
                  </label>
                  <p className="text-sm font-medium text-slate-900" data-id="element-4752" data-mp="3jmmt9bamhsrj">
                    {viewItem.code}
                  </p>
                </div>
                <div data-id="element-4753" data-mp="39y4uezlwlvqj">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-4754" data-mp="39xlfzsvgpph9">
                    Tipologia
                  </label>
                  <p className="text-sm font-medium text-slate-900" data-id="element-4755" data-mp="34wxo74zet4lz">
                    {viewItem.type || '-'}
                  </p>
                </div>
                <div className="col-span-full" data-id="element-4756" data-mp="2vy3srhxteka0">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-4757" data-mp="2w0gbvyfb70on">
                    Nome
                  </label>
                  <p className="text-sm font-medium text-slate-900" data-id="element-4758" data-mp="2m0z7rlfpguus">
                    {viewItem.name || '-'}
                  </p>
                </div>
                {viewItem.description && <div className="col-span-full" data-id="element-4759" data-mp="k8jeav3w0wlz">
                    <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-4760" data-mp="k6q81gx6seei">
                      Descrizione
                    </label>
                    <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-4761" data-mp="wqdexy9o44nc">
                      {viewItem.description}
                    </p>
                  </div>}
                <div data-id="element-4762" data-mp="16gop42jy3a2o">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-4763" data-mp="16j2mwj60f0dx">
                    Responsabile
                  </label>
                  <p className="text-sm font-medium text-slate-900" data-id="element-4764" data-mp="dtlzjzx8y9ok">
                    {viewItem.responsible || '-'}
                  </p>
                </div>
                <div data-id="element-4765" data-mp="nqguy9y83tym">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-4766" data-mp="nr0axl4bc8a0">
                    Tipologia manutenzione
                  </label>
                  <Badge variant={viewItem.maintenanceType === 'Ordinaria' ? 'info' : 'warning'} data-id="element-4767" data-mp="1z5vqraep4fs5">
                    {viewItem.maintenanceType}
                  </Badge>
                </div>
              </div>
            </section>

            <section data-id="element-4768" data-mp="2u1bzy9t69912">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4769" data-mp="2u1kf68croqff">
                <Calendar className="h-4 w-4" data-id="element-4770" data-mp="3305t1q7anh9v" /> Manutenzione programmata
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4771" data-mp="2yyubm7ykq2cr">
                <div data-id="element-4772" data-mp="2yykn6ot6g396">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-4773" data-mp="3cyllbwi24wky">
                    Ultima manutenzione
                  </label>
                  <p className="text-sm font-medium text-slate-900" data-id="element-4774" data-mp="3801wnoc2x6ow">
                    {viewItem.lastMaintenance || '-'}
                  </p>
                </div>
                <div data-id="element-4775" data-mp="1dmarp24p3azs">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-4776" data-mp="1dmsvcjtosrhq">
                    Prossima scadenza
                  </label>
                  <p className="text-sm font-medium text-slate-900" data-id="element-4777" data-mp="3u7o5wpmsn8dq">
                    {viewItem.nextMaintenance || '-'}
                  </p>
                </div>
                <div data-id="element-4778" data-mp="3fgytxl1idp8t">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-4779" data-mp="3ki4lvgnch8ha">
                    Ripetibilità
                  </label>
                  <p className="text-sm font-medium text-slate-900" data-id="element-4780" data-mp="36gs7varqvgy1">
                    {viewItem.repeatability || '-'}
                  </p>
                </div>
              </div>
            </section>

            <section data-id="element-4781" data-mp="2wlqlgmoussz5">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4782" data-mp="2wmjrflfzneg8">
                <FileText className="h-4 w-4" data-id="element-4783" data-mp="ut2cdwpj3bbg" /> Allegati e note
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4784" data-mp="uqzkf74tf843">
                <div data-id="element-4785" data-mp="zr45zbkkyt16">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-4786" data-mp="zpj9hgeph00o">
                    Allegati
                  </label>
                  <AttachmentsList files={viewItem.attachments || []} data-id="element-4787" data-mp="283tpak5luo5n" />
                </div>
                <div data-id="element-4788" data-mp="1ucg9nhatiafw">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-4789" data-mp="1ucygf7v3w722">
                    Note
                  </label>
                  <p className="text-sm text-slate-700 whitespace-pre-line" data-id="element-4790" data-mp="2n0c4eggttsa1">
                    {viewItem.notes || '-'}
                  </p>
                </div>
              </div>
            </section>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-4791" data-mp="2d24ycso2sumt">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-4792" data-mp="2i0zgdse7yf5v">
                Chiudi
              </Button>
              <Button onClick={handleViewToEdit} startContent={<Edit className="h-4 w-4" data-id="element-4794" data-mp="16jg8hmpp1o0z" />} data-id="element-4793" data-mp="16lj88ygneviw">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica asset' : 'Nuovo asset'} size="2xl" data-id="element-4795" data-mp="u5b5iowaytwp">
        <AnimatePresence mode="wait" data-id="element-4796" data-mp="134esw52i5vh0">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-4797" data-mp="fdj4zj163kf1">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-4798" data-mp="kc2olcsagk48">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-4799" data-mp="21h137qwpqdd0" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-4800" data-mp="26fm3trfvmz1z">
                Asset salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-4801" data-mp="18u7xsv8dopwo">
              <div className="space-y-8" data-id="element-4802" data-mp="1isdxr7amjlyw">
                <section data-id="element-4803" data-mp="1msee4xhaa78l">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4804" data-mp="1mso1seeuw2b4">
                    <Wrench className="h-4 w-4" data-id="element-4805" data-mp="2u7j2shy89wlo" /> Dettagli asset
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4806" data-mp="2y5gvhur7j0tt">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} placeholder="EQP-001" data-id="element-4807" data-mp="min2u6jsid7p" />

                    {/* Custom Creatable Select Implementation */}
                    <div className="flex flex-col gap-1.5" data-id="element-4808" data-mp="3uzhx4xmiupa6">
                      <label className="block text-sm font-medium text-slate-700" data-id="element-4809" data-mp="3uy6qmq1oz3fd">
                        Tipologia <span className="text-red-500" data-id="element-4810" data-mp="3qyg2yv7gp3f2">*</span>
                      </label>
                      <input list="equipment-types" className="block w-full rounded-xl border-2 border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-seform-orange focus:bg-white focus:ring-0 focus:outline-none" value={formData.type || ''} onChange={e => setFormData({
                    ...formData,
                    type: e.target.value
                  })} placeholder="Seleziona o scrivi nuovo..." data-id="element-4811" data-mp="3m04ryh5fkmv9" />
                      <datalist id="equipment-types" data-id="element-4812" data-mp="3uanrpzubrt2n">
                        {equipmentTypes.map(type => <option key={type} value={type} data-id="element-4813" data-mp="2ycoji0057w7" />)}
                      </datalist>
                    </div>

                    <Input label="Nome" placeholder="Es. Fonometro" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required className="col-span-full" data-id="element-4814" data-mp="3pc30ur0yqlqv" />

                    <div className="col-span-full" data-id="element-4815" data-mp="2rt5treq87lm0">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} data-id="element-4816" data-mp="36p3jof9k3tps" />
                    </div>

                    <Input label="Responsabile" placeholder="Nome responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-4817" data-mp="3pe0jeynq3efe" />

                    <Select label="Tipologia manutenzione" value={formData.maintenanceType || 'Ordinaria'} onChange={e => setFormData({
                  ...formData,
                  maintenanceType: e.target.value
                })} options={[{
                  value: 'Ordinaria',
                  label: 'Ordinaria'
                }, {
                  value: 'Straordinaria',
                  label: 'Straordinaria'
                }]} data-id="element-4818" data-mp="59m5f9uelzth" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-4819" data-mp="2zttw5faognh2"></div>

                <section data-id="element-4820" data-mp="34rdfuuq59sge">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4821" data-mp="34soigr7usu5x">
                    <Calendar className="h-4 w-4" data-id="element-4822" data-mp="1h4zjswfacdrx" /> Manutenzione programmata
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4823" data-mp="1d2w4txteqr1w">
                    <DatePicker label="Prossima scadenza" value={formData.nextMaintenance || ''} onChange={e => setFormData({
                  ...formData,
                  nextMaintenance: e.target.value
                })} data-id="element-4824" data-mp="1d11g08f0mesj" />
                    <Select label="Ripetibilità" value={formData.repeatability || ''} onChange={e => setFormData({
                  ...formData,
                  repeatability: e.target.value
                })} options={[{
                  value: '3 mesi',
                  label: '3 mesi'
                }, {
                  value: '6 mesi',
                  label: '6 mesi'
                }, {
                  value: '1 anno',
                  label: '1 anno'
                }, {
                  value: '2 anni',
                  label: '2 anni'
                }]} data-id="element-4825" data-mp="1vrsrbfikcf9k" />
                    <div className="col-span-full flex items-center gap-2" data-id="element-4826" data-mp="2ohs25omfd3fp">
                      <input type="checkbox" id="sendEmailReminder" className="rounded text-seform-orange focus:ring-seform-orange" checked={formData.sendEmailReminder || false} onChange={e => setFormData({
                    ...formData,
                    sendEmailReminder: e.target.checked
                  })} data-id="element-4827" data-mp="2tgcvr5768ov6" />
                      <label htmlFor="sendEmailReminder" className="text-sm text-slate-700 flex items-center gap-1" data-id="element-4828" data-mp="1kameix5yelil">
                        <Mail className="h-4 w-4 text-slate-400" data-id="element-4829" data-mp="rk47jl23zzzw" />
                        Invia promemoria email alla scadenza
                      </label>
                    </div>
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-4830" data-mp="wj74sjbul1xb"></div>

                <section data-id="element-4831" data-mp="whdzp8gvuprf">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4832" data-mp="wlbhnqrq5b9q">
                    <FileText className="h-4 w-4" data-id="element-4833" data-mp="27yne3gl5mrlv" /> Allegati e note
                  </h4>
                  <div className="space-y-6" data-id="element-4834" data-mp="2mkf9585ntas4">
                    <AttachmentsField value={formData.attachments || []} onChange={attachments => setFormData({
                  ...formData,
                  attachments
                })} data-id="element-4835" data-mp="2mm9uuoor96x9" />
                    <Textarea label="Note" value={formData.notes || ''} onChange={e => setFormData({
                  ...formData,
                  notes: e.target.value
                })} data-id="element-4836" data-mp="27no86ugc7r0e" />
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-4837" data-mp="2vkbryt5dbff9">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-4838" data-mp="2qn23bfpsz7sh">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-4840" data-mp="3rkwz62xgyk0d" />} data-id="element-4839" data-mp="3j9ejqttpuonh">
                    Salva asset
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Maintenance Modal */}
      <Modal isOpen={isMaintenanceModalOpen} onClose={() => setIsMaintenanceModalOpen(false)} title="Registra manutenzione" size="lg" data-id="element-4841" data-mp="9eibvddb8xpb">
        <div className="space-y-6" data-id="element-4842" data-mp="ienihlaojs2f">
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 mb-4" data-id="element-4843" data-mp="idvoug0n84es">
            <h3 className="font-bold text-seform-navy" data-id="element-4844" data-mp="n4vjfemaiae4">{selectedItem?.name}</h3>
            <p className="text-sm text-slate-500" data-id="element-4845" data-mp="s4grqfncnv3i">
              Codice: {selectedItem?.code} • Ultima manutenzione:{' '}
              {selectedItem?.lastMaintenance}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4846" data-mp="122ny64k0iur9">
            <DatePicker label="Data esecuzione" value={maintenanceData.date || ''} onChange={e => setMaintenanceData({
            ...maintenanceData,
            date: e.target.value
          })} data-id="element-4847" data-mp="122w7xnku9jy8" />
            <Select label="Esito" value={maintenanceData.result || ''} onChange={e => setMaintenanceData({
            ...maintenanceData,
            result: e.target.value
          })} options={[{
            value: 'Positivo',
            label: 'Positivo'
          }, {
            value: 'Negativo',
            label: 'Negativo'
          }]} required data-id="element-4848" data-mp="m6er9c1l6mvy" />
            <Select label="Tipo manutenzione" value={maintenanceData.type || 'Ordinaria'} onChange={e => setMaintenanceData({
            ...maintenanceData,
            type: e.target.value
          })} options={[{
            value: 'Ordinaria',
            label: 'Ordinaria'
          }, {
            value: 'Straordinaria',
            label: 'Straordinaria'
          }]} required data-id="element-4849" data-mp="36v2rjam2f426" />
            <DatePicker label="Prossima scadenza" value={maintenanceData.nextDate || ''} onChange={e => setMaintenanceData({
            ...maintenanceData,
            nextDate: e.target.value
          })} data-id="element-4850" data-mp="ylmmom7y7ri9" />
            <div className="col-span-full" data-id="element-4851" data-mp="tpo09466kjkh">
              <Textarea label="Note / Interventi effettuati" value={maintenanceData.notes || ''} onChange={e => setMaintenanceData({
              ...maintenanceData,
              notes: e.target.value
            })} required data-id="element-4852" data-mp="2j3supop4fd4h" />
            </div>
            <div className="col-span-full flex items-center gap-2" data-id="element-4853" data-mp="eyet4tjn8tbo">
              <input type="checkbox" id="maintenanceEmailReminder" className="rounded text-seform-orange focus:ring-seform-orange" checked={maintenanceData.sendEmailReminder || false} onChange={e => setMaintenanceData({
              ...maintenanceData,
              sendEmailReminder: e.target.checked
            })} data-id="element-4854" data-mp="f0r9515uctm3" />
              <label htmlFor="maintenanceEmailReminder" className="text-sm text-slate-700 flex items-center gap-1" data-id="element-4855" data-mp="3ngaznsf4nxxg">
                <Mail className="h-4 w-4 text-slate-400" data-id="element-4856" data-mp="3dgab9s2eirme" />
                Invia promemoria email per prossima scadenza
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-4857" data-mp="1xxm5qa0ncju1">
            <Button variant="flat" color="default" onClick={() => setIsMaintenanceModalOpen(false)} data-id="element-4858" data-mp="1xzff3x2n9i5q">
              Annulla
            </Button>
            <Button onClick={saveMaintenance} startContent={<CheckCircle2 className="h-4 w-4" data-id="element-4860" data-mp="1a9l47l1vy3o4" />} data-id="element-4859" data-mp="26vy633rbevzo">
              Registra
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-4861" data-mp="1j7wshgfa4pde">
        <AnimatePresence mode="wait" data-id="element-4862" data-mp="ri21zgfoqtn5">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-4863" data-mp="rfpll7pksrc7">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-4864" data-mp="23krinbvo1egm">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-4865" data-mp="1ksyrln05q3bj" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-4866" data-mp="3097bbgu2o3xy">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-4867" data-mp="33sohzdzv8qpk">
              <div className="space-y-6" data-id="element-4868" data-mp="2tvto58ejfehm">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-4869" data-mp="2twv1jjj10d2i">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-4870" data-mp="2yvxtc29zwmsj" />
                  <div data-id="element-4871" data-mp="3mjxvdycrbzmf">
                    <p className="text-sm text-red-700 font-medium" data-id="element-4872" data-mp="3mlinzifih8hn">
                      Attenzione: questa azione è irreversibile.
                    </p>
                    <p className="text-sm text-red-600 mt-1" data-id="element-4873" data-mp="3csl53b6lxhii">
                      L'asset <strong data-id="element-4874" data-mp="3hsfqiz7m5ew6">{itemToDelete?.name}</strong> verrà
                      eliminato definitivamente.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-4875" data-mp="e3m8vv5yovk9">
                  <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-4876" data-mp="e5729hgji8gj">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} startContent={<Trash2 className="h-4 w-4" data-id="element-4878" data-mp="1wfvz8kotdz19" />} data-id="element-4877" data-mp="21egnrmupeu9v">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}